/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import _ from 'underscore';


// Functions to extract layouts, widgets from a card

function getLayouts(card, boardModel) {
	const layouts = [];
	card.items.forEach(item => {
		if(item.type === 'widget') {
			const id = item.id;
			const widgetModel = boardModel.getWidgetModel(id);
			if(widgetModel && widgetModel.type === 'placeholder') {
				layouts.push( { placeholder : item } );
			} else {
				layouts.push( { widgetLayout : item } );
			}
		}
	});
	return layouts;
}


function getVisualizations(card, boardModel) {
	return getLayouts(card, boardModel).map(layout => layout.widgetLayout || null);
}

function getVisualizationsIncludingEmpty(card, boardModel) {
	return getLayouts(card, boardModel).map(layout => layout.widgetLayout || layout.placeholder);
}

function getVisualization(card, boardModel, index) {
	const layouts = getLayouts(card, boardModel);
	return (index < layouts.length && layouts[index].widgetLayout) || null;
}

// Functions to create new cards

function _getPayload(payload) {
	return _.isString(payload) ? {
		undoRedoTransactionId: _.uniqueId(payload)
	} : payload;
}

// Function to duplicate cards
//Utility function to duplicate widget instance and layout which references it.
function duplicateVisualizationsInCard(card, {
	boardModel,
	extraWidgetPayload,
	widgetIgnoredProps = []
}) {
	const idMap = {};

	//Duplicate widgets in card
	const cardWidgets = boardModel._cloneWidgets(card, idMap);

	return getVisualizationsIncludingEmpty(card, boardModel).map( (vis) => {
		if(vis) {
			const newWidget = cardWidgets[idMap[vis.id]];

			widgetIgnoredProps.forEach(prop => {
				delete newWidget[prop];
			});

			const oldProperties = newWidget.properties;
			Object.assign(newWidget, extraWidgetPayload);
			if (oldProperties && extraWidgetPayload && extraWidgetPayload.properties) {
				// make copy of properties array, as it was copied by reference in Object.assign(...)
				newWidget.properties = [...newWidget.properties];
				const newPropertyKeys = newWidget.properties.map(prop => prop.id);
				// add in all oldProperties that were not part of the payload
				newWidget.properties.unshift(...oldProperties.filter(prop => !newPropertyKeys.includes(prop.id)));
			}

			const widgetLayout = vis.cloneLayout(idMap).toJSON();
			delete widgetLayout.relatedLayouts;
			return {
				layout: widgetLayout,
				widget: newWidget
			};
		} else {
			return null;
		}
	});
}

// --- CREATE


function _createPlaceHolderWidgetSpec() {
	return {
		model: {
			type: 'placeholder',
			placeHolder:  true  //not needed per se, used only for helping debug
		}
	};
}

function _createCard(exploreLayout, options) {

	const {
		extraLayoutPayload: {
			model: optionsModel = {}
		} = {}
	} = options;

	const boardModel = exploreLayout.boardModel;

	const model = Object.assign({
		type: 'exploreCard',
		items: options.layouts
	}, optionsModel);
	const layoutPayload = Object.assign(
		{},
		options.extraLayoutPayload || {},
		{
			model
		}
	);

	const pageSpec = {
		addLayoutArray: [layoutPayload],
		parentId: exploreLayout.id
	};
	if(options.widgetSpecMap) {
		pageSpec.widgetSpecMap = options.widgetSpecMap;
	}
	const sender = options && options.extraLayoutPayload && options.extraLayoutPayload.sender;
	const addLayoutData = boardModel.addLayouts(pageSpec, sender ? sender : 'CardModelUtil', options.payload);
	const updatedExploreSpec = addLayoutData.value.parameter;

	return updatedExploreSpec.addLayoutArray[0].model.id;
}

function createCardFromFragment(exploreLayout, widgetSpec, payload) {
	const options = {
		insertBefore: null,
		parentId: exploreLayout.id,
		model: widgetSpec.model
	};
	const event = exploreLayout.addFragment(options, 'createCardFromFragment', payload);
	return event.value.parameter.model.layout[0].id;
}

function createCardFromSpec(exploreLayout, widgetSpecs, options) {
	const payload = _getPayload(options.payload);
	if (widgetSpecs[0] && widgetSpecs[0].specType && widgetSpecs[0].specType === 'fragment') {
		return createCardFromFragment(exploreLayout, widgetSpecs[0], payload);
	}
	if(widgetSpecs[0] && widgetSpecs[0].model){
		delete widgetSpecs[0].model.titleHtml;
	}
	const placeholders = undefined;
	const newCardLayoutId = _createCard(
		exploreLayout, {
			layouts: placeholders,
			payload: payload,
			extraLayoutPayload: options.extraLayoutPayload
		}
	);
	//Add visualizations to card
	const layoutModel = exploreLayout.boardModel.layout;
	const newLayoutModel = layoutModel.findChildItem(layoutModel.items, newCardLayoutId);
	for(let i = 0; i < widgetSpecs.length; i++) {
		if(widgetSpecs[i]) {
			newLayoutModel.placeWidgetInCard(widgetSpecs[i], payload);
		} else {
			newLayoutModel.placeWidgetInCard( _createPlaceHolderWidgetSpec(), payload);
		}
	}
	return newCardLayoutId;
}

function createCardFromExistingCards(exploreLayout, cards, options) {

	const {
		payload: _payload,
		extraLayoutPayload,
		extraWidgetPayload,
		widgetIgnoredProps
	} = options;

	const payload = _getPayload(_payload);
	const boardModel = exploreLayout.boardModel;
	const widgetSpecMap = {};
	const newWidgetIdsBySourceCard = {};
	const layouts = [];

	cards.forEach(card => {
		const duplicatedVisualizations = duplicateVisualizationsInCard(card, {
			boardModel,
			extraWidgetPayload,
			widgetIgnoredProps
		}) || [];

		newWidgetIdsBySourceCard[card.getId()]=[];
		duplicatedVisualizations
			.forEach((viz) => {
				widgetSpecMap[viz.widget.id] = viz.widget;
				layouts.push(viz.layout);
				if (viz.layout && viz.layout.id) {
					newWidgetIdsBySourceCard[card.getId()].push(viz.layout.id);
				}
			});
	});

	//Create the target card and assign all of the widgets to it.
	const newLayoutId = _createCard(
		exploreLayout, {
			layouts,
			widgetSpecMap,
			payload,
			extraLayoutPayload
		}
	);

	createEventGroupsForWidgetsInTheTargetCard(exploreLayout, newLayoutId, newWidgetIdsBySourceCard, payload);
	return newLayoutId;
}

function createEventGroupsForWidgetsInTheTargetCard(exploreLayout, newLayoutId, newWidgetIdsBySourceCard, payload) {
	//Manage the event groups for the target card (eg: a compare card) based on the cards they came from.
	//Place each card in the comparison into its own event group to ensure they remain as they were.
	Object.keys(newWidgetIdsBySourceCard).forEach((sourceCardId) => {
		const targetWidgetIds = newWidgetIdsBySourceCard[sourceCardId];	//Note: currently only 1 widget per source card.
		if (targetWidgetIds.length) {
			const newEventGroupId = _initializeEventGroup(exploreLayout.boardModel, /*targetCard*/ newLayoutId, /*targetWidgetIds*/ targetWidgetIds, payload.undoRedoTransactionId);

			//Rescope the pageContext entries and ensure any brushing entries are placed into the right event group.
			copyPageContextAndRescope(exploreLayout, {
				layoutId: sourceCardId,
				cloneId: newLayoutId,
				eventGroupId: newEventGroupId,
				undoRedoTransactionId: payload.undoRedoTransactionId
			});
		}
	});

	return newLayoutId;
}


/**
 * Delete an array of cards
 * @param  {string[]} ids, An array of card IDs to be deleted.
 * @return {boolean} True if it successfully deleted passed in cards
 */
function deleteCards(exploreLayout, ids) {
	if (!ids || !ids.length) {
		return false;
	}

	const payload = {
		undoRedoTransactionId: _.uniqueId(ids.length === 1 ? 'removeSingleCard' : 'removeMultipleCards')
	};

	//The following line returns an object that has information about removed objects
	exploreLayout.boardModel.removeLayouts(ids, 'ExploreCardList', payload);

	return true;
}

/**
 * Delete given visualization and select next vis if it's not a placeholder
 * @param  {Object} exploreLayout is layout of exploration
 * @param  {Array} ids of target widget(s) for deletion
 * @return {boolean} on successful deletion of given visualization
 */
const deleteVisAndSelectRemainingVis = (exploreLayout, ids) => {
	if (!ids || !ids.length) {
		return false;
	}
	const dashboardApi = exploreLayout.dashboardApi;
	const currentContentView = dashboardApi.getCurrentContentView();
	const exploreContainerModel = currentContentView.getExploreContainerLayoutModel();

	const transaction = dashboardApi.getFeature('Transaction');
	const transactionToken = transaction.startTransaction();

	let parentLayoutModel, targetWidgetId;
	ids.forEach((id) => {
		const childLayoutModel = exploreContainerModel.findModel(id);
		if (childLayoutModel) {
			parentLayoutModel = childLayoutModel.getParent();
			parentLayoutModel.replaceWidgetInCard(_createPlaceHolderWidgetSpec(), id, { transactionToken });
		}
	});

	if (parentLayoutModel.isCompareEnabled) {
		const cardLayouts = getLayouts(parentLayoutModel, exploreLayout.boardModel) || [];
		cardLayouts.forEach((layout) => {
			if (layout.hasOwnProperty('widgetLayout') && ids.indexOf(layout.widgetLayout.id) === -1) { // find remaining non-placeholder widget id
				targetWidgetId = layout.widgetLayout.id;
			}
		});
		if (targetWidgetId && exploreContainerModel.findModel(targetWidgetId)) {
			dashboardApi.getCanvas().selectContent([targetWidgetId]);
		}
	}

	transaction.endTransaction(transactionToken);

	return true;
};

/**
 * Copy page contexts items from the old scope to the new scope
 * @param {Object} exploreLayout
 * @param {Object} options { cloneId: newLayoutId, layoutId: oldLayoutId}
 */
function copyPageContextAndRescope(exploreLayout, options){
	if(exploreLayout.boardModel.pageContext && options){
		exploreLayout.boardModel.pageContext.copyPageContextItemsAndRescope(options);
	}
}

function _createCards(exploreLayout, options) {

	const optionsArray = Array.isArray(options) ? options : [options];

	const boardModel = exploreLayout.boardModel;

	const widgetSpecMap = {};
	const layoutPayloads = optionsArray
		.map(options => {

			const {
				extraLayoutPayload: {
					model: optionsModel = {}
				} = {},
				widgetSpecMap: _widgetSpecMap
			} = options;

			// merge all widget spec maps from all the cards
			Object.keys(_widgetSpecMap)
				.forEach(key => widgetSpecMap[key] = _widgetSpecMap[key]);

			const model = Object.assign({
				type: 'exploreCard',
				items: options.layouts
			}, optionsModel);
			const layoutPayload = Object.assign(
				{},
				options.extraLayoutPayload || {},
				{
					model
				}
			);

			return layoutPayload;
		});

	const pageSpec = {
		addLayoutArray: layoutPayloads,
		parentId: exploreLayout.id,
		widgetSpecMap
	};

	const transactionId = options.length && options[0].payload && options[0].payload.undoRedoTransactionId;
	const addLayoutData = boardModel.addLayouts(pageSpec,
		'CardModelUtil',
		{ undoRedoTransactionId: transactionId }
	);
	const updatedExploreSpec = addLayoutData.value.parameter;

	return updatedExploreSpec.addLayoutArray
		.map((addLayout, index) => {
			void(layoutPayloads);
			const newLayoutId = addLayout.model.id;
			const originalCard = optionsArray[index] && optionsArray[index].card;
			const thisWidgetSpecMap = optionsArray[index] && optionsArray[index].widgetSpecMap;
			if (originalCard) {
				_initializeEventGroupsForDuplicatedCard(boardModel,
					/*sourceWidgetIds*/ originalCard.items.map((item) => item.id),
					/*targetCard*/ newLayoutId,
					/*targetWidgets*/ Object.keys(thisWidgetSpecMap),
					transactionId
				);

				//When duplicating, we simply need to rescope (change the pageId)
				//(ie: if a source filter was in group 2, its still in group 2)
				copyPageContextAndRescope(exploreLayout, {
					layoutId: originalCard.id,
					cloneId: newLayoutId,
					undoRedoTransactionId: transactionId
				});
			}
			return newLayoutId;
		});
}

function _initializeEventGroupsForDuplicatedCard(boardModel, sourceWidgetIds, targetCardId, targetWidgetIds, transactionId) {
	//Build a map of assignments from the original eventGroup to the source and target widgetIds
	const matchingEventGroupMap = {};
	boardModel.eventGroups.getModels().forEach((eventGroup) => {
		const eventGroupId = eventGroup.id;
		eventGroup.widgetIds.forEach((widgetId) => {
			const widgetIdx = sourceWidgetIds.indexOf(widgetId);
			if (widgetIdx >= 0) {
				matchingEventGroupMap[eventGroupId] = matchingEventGroupMap[eventGroupId] || [];
				matchingEventGroupMap[eventGroupId].push({ sourceWidgetId: sourceWidgetIds[widgetIdx], targetWidgetId: targetWidgetIds[widgetIdx] });
			}
		});
	});

	//For each source eventGroup, create a new group with the new widgetId
	//(to keep scope limited, only do this for the compare card with multiple groups case - otherwise, let things default.).
	if (Object.keys(matchingEventGroupMap).length > 1) {
		Object.values(matchingEventGroupMap).forEach((eventGroupMapping) => {
			const targetWidgetIds = eventGroupMapping.map((eventGroupMapping) => eventGroupMapping.targetWidgetId);
			_initializeEventGroup(boardModel, targetCardId, targetWidgetIds, transactionId);
		});
	}
}

function _initializeEventGroup(boardModel, targetCardId, targetWidgetIds, transactionId) {
	const newEventGroup = boardModel.eventGroups.createGroup(
		targetWidgetIds,
		{ targetPageId: targetCardId, payloadData: { undoRedoTransactionId: transactionId, silent:true } });
	return newEventGroup && newEventGroup.id || null;
}

function duplicateCards(exploreLayout, options) {

	const optionsArray = Array.isArray(options) ? options : [options];

	const boardModel = exploreLayout.boardModel;

	const createCardOptions = optionsArray
		.map(({ card, options }) => {
			const {
				payload: _payload,
				extraLayoutPayload = {},
				extraWidgetPayload
			} = options;

			const widgetSpecMap = {};
			const layouts = [];
			const payload = _getPayload(_payload);

			const duplicatedVisualizations = duplicateVisualizationsInCard(card, {
				boardModel,
				extraWidgetPayload
			}) || [];

			duplicatedVisualizations
				.forEach((viz) => {
					widgetSpecMap[viz.widget.id] = viz.widget;
					layouts.push(viz.layout);
				});

			return {
				card,
				layouts,
				widgetSpecMap,
				payload,
				extraLayoutPayload
			};
		});

	const newLayoutIds = _createCards(
		exploreLayout, createCardOptions
	);
	return newLayoutIds;
}

export default {
	getLayouts,
	getVisualizations,
	getVisualization,
	createCardFromSpec,
	createCardFromExistingCards,
	createEventGroupsForWidgetsInTheTargetCard,
	deleteCards,
	deleteVisAndSelectRemainingVis,
	copyPageContextAndRescope,
	duplicateCards
};



// WEBPACK FOOTER //
// ./src/js/core/CardModelUtil.js